import { Button, Layout, Menu } from 'antd'
import {
  AreaChartOutlined,
  FileSearchOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContext, useState, ReactNode } from 'react'
import { AuthContext } from '../../contexts/AuthContext/definition'

const { Sider } = Layout

import styles from './index.module.css'

import turbobunny from '../../assets/turbobunny.webp'
import MoneybagIcon from './moneybagIcon'
import { SettingsContext } from '../../contexts/SettingsContext/definition'
import SettingsHelper from '../../lib/helpers/settings'

export interface LayoutSiderProps {
  collapsed: boolean
}

const LayoutSider = ({ collapsed }: LayoutSiderProps) => {
  const [underBreakpoint, setUnderBreakpoint] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = useContext(AuthContext)
  const { settings } = useContext(SettingsContext)
  const settingsChecker = new SettingsHelper(settings)

  const menuItems = [
    {
      key: '/rebuttal',
      icon: <PlayCircleOutlined />,
      label: 'Rebuttal',
    },
    {
      key: '/kpi',
      icon: <AreaChartOutlined />,
      label: 'KPI',
    },
    {
      key: '/myfiles',
      icon: <FileSearchOutlined />,
      label: 'My Files',
    },
    settingsChecker.check('location') === 'us' && {
      key: '/attendance',
      icon: <MoneybagIcon />,
      label: 'Champion Bonus',
    },
    hasPermission('agents:ui:path:read:/sms') && {
      key: '/sms',
      icon: <MessageOutlined />,
      label: 'SMS',
    },
    (hasPermission('agents:ui:path:read:/admin/users') ||
      hasPermission('agents:ui:path:read:/admin/roles') ||
      hasPermission('agents:ui:path:read:/admin/permissions')) && {
      key: 'admin',
      icon: <SettingOutlined />,
      label: 'Admin',
      children: [
        hasPermission('agents:ui:path:read:/admin/users') && {
          key: '/admin/users',
          label: 'Users',
        },
        hasPermission('agents:ui:path:read:/admin/roles') && {
          key: '/admin/roles',
          label: 'Roles',
        },
        hasPermission('agents:ui:path:read:/admin/permissions') && {
          key: '/admin/permissions',
          label: 'Permissions',
        },
      ].filter(Boolean),
    },
    {
      key: '/logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ].filter(Boolean) as {
    key: string
    icon: ReactNode
    label: string
  }[]

  return (
    <Sider
      breakpoint="md"
      collapsedWidth={underBreakpoint ? 0 : undefined}
      onBreakpoint={(broken) => {
        setUnderBreakpoint(broken)
      }}
      className={styles.sider}
      trigger={null}
      collapsible={!underBreakpoint}
      collapsed={collapsed}
    >
      <div className={styles.logoVertical}>
        <div onClick={() => navigate('/')}>
          {!underBreakpoint && collapsed ? (
            <img src={turbobunny} alt="Turbodebt Interal" />
          ) : (
            <svg
              width="207"
              height="28"
              viewBox="0 0 207 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M95.6042 6.21533H89.2314L87.689 19.3821H91.4254L91.892 15.3942H93.5466L95.7951 19.3821H100.089L97.4103 14.7912C99.1679 14.0942 100.419 12.7609 100.659 10.7275V10.6911C100.813 9.39415 100.525 8.39717 99.8285 7.62444C99.0255 6.74261 97.6648 6.21533 95.6042 6.21533ZM96.8982 10.9366V10.973C96.783 11.9336 95.9618 12.5336 94.7103 12.5336H92.2254L92.5981 9.35476H95.0618C96.2951 9.35476 97.0224 9.88507 96.8982 10.9366Z"
                fill="#63CE4D"
              ></path>
              <path
                d="M48.3155 6.21533H63.9217L63.546 9.41537H59.5035L58.3368 19.3852H54.6004L55.0822 15.2942H53.8247L52.6943 12.0972H55.455L55.7701 9.41537H49.4458L48.3155 6.21533Z"
                fill="#63CE4D"
              ></path>
              <path
                d="M78.7034 6.21533H82.4762L81.6216 13.5154C81.1247 17.7488 78.4216 19.6306 74.5913 19.6306C70.7609 19.6306 68.5942 17.7306 69.0791 13.6124L69.9458 6.21836H73.7185L72.8609 13.5366C72.6397 15.4366 73.5155 16.3397 75.0155 16.3397C76.5155 16.3397 77.6186 15.476 77.8367 13.6306L78.7034 6.21533Z"
                fill="#63CE4D"
              ></path>
              <path
                d="M114.41 6.21533H107.498L105.955 19.3821H112.907C116.122 19.3821 118.171 18.0094 118.434 15.7518V15.7154C118.646 13.9275 117.713 13.0245 116.137 12.4972C117.392 11.9881 118.383 11.0851 118.561 9.56385V9.52749C118.671 8.62445 118.425 7.92747 117.949 7.38201C117.249 6.63049 116.104 6.21533 114.41 6.21533ZM114.761 15.2094V15.2457C114.668 16.0185 113.983 16.4488 112.828 16.4488H109.94L110.225 14.0215H113.074C114.322 14.0245 114.843 14.4942 114.761 15.2094ZM114.798 10.279V10.3154C114.704 11.0881 114.007 11.4639 112.871 11.4639H110.522L110.792 9.1487H113.237C114.334 9.15173 114.886 9.54567 114.798 10.279Z"
                fill="#63CE4D"
              ></path>
              <path
                d="M131.549 5.95166C127.392 5.95166 123.913 9.01836 123.471 12.8002V12.8366C123.025 16.6184 125.749 19.6458 129.91 19.6458C134.071 19.6458 137.546 16.579 137.989 12.7972V12.7608C138.434 8.982 135.71 5.95166 131.549 5.95166ZM134.156 12.8002V12.8366C133.931 14.7366 132.374 16.3548 130.334 16.3548C128.295 16.3548 127.062 14.7002 127.286 12.8002V12.7639C127.51 10.8638 129.068 9.24564 131.128 9.24564C133.189 9.24564 134.38 10.882 134.156 12.8002Z"
                fill="#63CE4D"
              ></path>
              <path
                d="M186.723 12.5759C187.835 12.1244 189.017 11.2395 189.217 9.52739V9.49102C189.32 8.64556 189.093 7.94858 188.602 7.38494C187.917 6.6516 186.757 6.21826 185.214 6.21826H179.478L177.935 19.3851H183.923C186.869 19.3851 188.969 18.0487 189.235 15.7911V15.7547C189.451 13.9486 188.266 13.0456 186.723 12.5759ZM180.823 7.55161H184.923C186.848 7.55161 187.808 8.39707 187.657 9.69709V9.73345C187.472 11.2759 186.108 12.085 184.278 12.085H180.293L180.823 7.55161ZM187.708 15.6608V15.6972C187.532 17.1638 186.16 18.0487 184.117 18.0487H179.593L180.138 13.4032H184.393C186.741 13.4032 187.878 14.2305 187.708 15.6608Z"
                fill="#828282"
              ></path>
              <path
                d="M163.041 6.21533H172.784L172.626 7.56989H164.405L163.877 12.0669H171.232L171.075 13.4215H163.72L163.181 18.0306H171.496L171.338 19.3852H161.499L163.041 6.21533Z"
                fill="#828282"
              ></path>
              <path
                d="M206.272 6.21533L206.111 7.58807H201.565L200.187 19.3852H198.665L200.044 7.58807H195.502L195.662 6.21533H206.272Z"
                fill="#828282"
              ></path>
              <path
                d="M149.589 6.21533H144.91L143.368 19.3821H148.047C152.283 19.3821 155.547 16.5033 155.98 12.7972V12.7609C156.416 9.05779 153.822 6.21533 149.589 6.21533ZM154.401 12.8003V12.8366C154.056 15.7518 151.616 18.0094 148.21 18.0094H145.053L146.274 7.58807H149.431C152.834 7.58807 154.741 9.88507 154.401 12.8003Z"
                fill="#828282"
              ></path>
              <path
                d="M28.6609 20.6457C28.4003 20.6275 28.0215 20.6487 27.5579 20.6851C27.5458 20.6851 27.5336 20.6881 27.5185 20.6881C26.7791 20.8033 26.0609 21.0154 25.276 21.1972C25.0851 21.2427 24.8912 21.2821 24.6881 21.3124C24.476 21.3336 24.3215 21.3881 23.9609 21.3457C23.7972 21.3214 23.6154 21.2457 23.473 21.1275C23.3215 21.0124 23.2336 20.873 23.1609 20.7396C23.0336 20.473 22.9972 20.2366 22.9699 20.0154C22.8972 19.1457 23.0003 18.4002 23.0427 17.6578C23.0669 17.2911 23.079 16.9305 23.0487 16.5972C23.0215 16.2729 22.9306 15.9881 22.7851 15.682C22.4942 15.079 22.0002 14.5517 21.4396 14.1274C20.2881 13.2729 18.8184 12.9668 17.2881 12.9123C18.7548 12.4244 20.482 12.6365 21.8578 13.5062C22.5457 13.9395 23.1669 14.5093 23.5881 15.2517C23.7972 15.6153 23.97 16.0578 24.0154 16.4911C24.0669 16.9153 24.0609 17.3153 24.0487 17.7063C24.0306 18.479 23.9215 19.2517 23.9881 19.9002C24.0033 20.0548 24.0397 20.1972 24.0791 20.276C24.1275 20.3487 24.1003 20.3305 24.1397 20.3457C24.1578 20.3608 24.3821 20.3426 24.5457 20.3214C24.7185 20.3002 24.9033 20.2942 25.0791 20.2396C26.6306 19.776 28.3246 19.0517 30.0579 18.1517C31.7579 17.2699 34.0852 17.3214 36.258 17.0002C38.4216 16.682 40.0095 16.1214 40.4671 14.9002C40.6489 14.4092 40.5217 13.5668 40.152 12.6214C40.1368 12.5789 40.1156 12.5365 40.0974 12.4941C39.9974 12.4092 39.855 12.3759 39.7277 12.4274C39.3792 12.5668 39.0125 12.791 38.7519 13.1486C38.358 13.685 37.1459 14.2608 34.4095 14.3517C33.6458 14.3759 33.1276 14.1759 32.7913 13.8608C32.264 13.3608 31.6367 12.982 30.9155 12.8789C30.864 12.8729 30.8155 12.8638 30.764 12.8577C30.1397 12.7638 29.5185 12.6517 28.9033 12.5183C27.673 12.2607 26.467 11.9153 25.3003 11.4971C26.5306 11.6698 27.7609 11.7547 28.9882 11.7577C29.6003 11.7607 30.2125 11.7456 30.8215 11.7122C31.1034 11.6971 31.3913 11.6759 31.6731 11.6516C32.1307 11.6122 32.5307 11.3668 32.8458 11.0304C32.8913 10.9819 32.9367 10.9395 32.9852 10.9031C33.8943 10.191 36.2277 9.87587 38.5307 10.1304H38.5277C38.1792 9.75768C37.8307 9.38495 37.5217 9.20616 37.1429 9.11222L36.7852 8.78807C36.6065 8.74261 36.4367 8.71836 36.2731 8.71533C35.1518 8.69415 34.1246 8.9487 33.1974 9.47915C32.6155 9.81549 32.0671 10.2245 31.5701 10.6942C31.5459 10.7184 31.5217 10.7426 31.5003 10.7638L31.2217 11.0153C31.2185 11.0184 31.2185 11.0214 31.2155 11.0245C30.564 11.6122 29.8972 12.1819 29.2155 12.7335C28.2155 13.5426 27.2003 14.3366 26.1578 15.1062C25.3215 15.7214 24.476 16.3214 23.6184 16.9031C22.6124 17.585 21.5791 18.2365 20.5276 18.8456C20.3245 18.9638 19.3397 19.5395 19.3397 19.5395C19.3397 19.5395 19.5548 19.5941 19.8276 19.6638C20.0881 19.7305 20.4002 19.8123 20.7245 19.9002C21.4699 20.0972 22.3124 20.3396 23.0185 20.579C23.364 20.6972 23.6821 20.8123 23.9579 20.9123C24.2336 21.0124 24.4245 21.0821 24.5094 21.1124L24.5125 21.1154C25.567 21.4912 26.6579 21.467 27.6427 21.0245C28.067 20.8336 28.4064 20.7093 28.6609 20.6457ZM33.5855 11.3912C32.7098 12.2336 33.0977 13.573 33.8734 13.6821C35.2765 13.8821 37.1098 13.5851 37.6826 13.1276C38.2553 12.67 38.8856 11.2942 38.1704 10.8639C37.6401 10.5487 34.3886 10.6184 33.5855 11.3912Z"
                fill="#63CE4D"
              ></path>
            </svg>
          )}
        </div>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        onClick={({ key }) => navigate(key)}
        items={menuItems}
      />
      <Button
        type="text"
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        // onClick={onClickCollapse}
        style={{
          bottom: 0,
          color: 'white',
          fontSize: '16px',
          height: 64,
          position: 'absolute',
          width: '100%',
        }}
      />
    </Sider>
  )
}

export default LayoutSider
